/// <amd-module name="TFS/Admin/Groups/Pivots/GroupMembers/Redux/GroupMembersPivotReducers" />
import { IGroupMembersPivotState, SelectableGraphMember } from "TFS/Admin/Groups/Common/Contracts";
import { MemberOperation, MembershipQuery, SearchStatus } from "TFS/Admin/Groups/Common/Constants";
import { GroupMembersPivotActionTypes, GroupMembersPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupMembersPivotActions";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { ObservableArray } from "VSS/Core/Observable";
import { EmptyGuidString } from "VSS/Core/Util/String";
import produce from "immer";

export function groupMembersReducer(state: IGroupMembersPivotState, action: GroupMembersPivotActions): IGroupMembersPivotState {
    return produce(state || {}, draft => {
        switch (action.type) {
            case GroupMembersPivotActionTypes.InitView: {
                draft.searchResult = {
                    keyword: "",
                    resultItems: [],
                    status: SearchStatus.None,
                    hasMore: false
                };
                draft.loading = true;
                draft.users = new ObservableArray<SelectableGraphMember>([]);
                draft.showDeleteDialog = false;
                draft.showMessageCard = false;
                draft.invalidMembers = [];
                draft.validMembers = [];
                draft.multiOperationStatuses = [];
                draft.queryMembers = {};
                draft.supportServicePrincipals = false;
                break;
            }

            case GroupMembersPivotActionTypes.InitialUserFetch: {
                var data = action.payload;

                draft.users = data;
                draft.queryMembers[MembershipQuery.Direct] = data;
                draft.loading = false;
                break;
            }

            case GroupMembersPivotActionTypes.GroupMembersLoadCompleted: {
                draft.loading = false;
                break;
            }

            case GroupMembersPivotActionTypes.SearchGroupMembers: {
                updateSearchStatus(draft, SearchStatus.Searching);
                break;
            }

            case GroupMembersPivotActionTypes.UpdateSearchStatus: {
                updateSearchStatus(draft, action.payload);
                break;
            }

            case GroupMembersPivotActionTypes.UpdateSearchResult: {
                updateSearchResult(draft, action.payload.keyword, action.payload.result, action.payload.hasMore);
                break;
            }

            case GroupMembersPivotActionTypes.DeleteDialogCancel: {
                draft.selectedItem = undefined;
                draft.showDeleteDialog = false;
                break;
            }

            case GroupMembersPivotActionTypes.DeleteGroupMemberClick: {
                draft.selectedItem = action.payload;
                draft.showDeleteDialog = true;
                break;
            }

            case GroupMembersPivotActionTypes.DismissMultiSummary: {
                draft.multiOperationStatuses = [];
                break;
            }

            case GroupMembersPivotActionTypes.GroupMemberOperationStatus: {
                var { operation, resultMessage, severity, selectedItems, multiOperationStatuses } = action.payload;
                draft.showMessageCard = true;
                draft.messageCardText = resultMessage;
                draft.messageCardSeverity = severity;

                if (multiOperationStatuses) {
                    draft.multiOperationStatuses = multiOperationStatuses;
                }

                draft.lastOperation = operation;

                switch (operation) {
                    case MemberOperation.Delete: {
                        if (severity === MessageCardSeverity.Info) {
                            const users = draft.users.value;
                            if (selectedItems && selectedItems.length > 0) {
                                for (let item of selectedItems) {
                                    users.splice(users.findIndex(x => x.descriptor == item.descriptor), 1);
                                }
                            }
                            draft.queryMembers[MembershipQuery.Direct] = new ObservableArray(users);
                            draft.queryMembers[MembershipQuery.Expanded] = undefined;
                        }
                        draft.showDeleteDialog = false;
                        break;
                    }

                    case MemberOperation.Add: {
                        if (selectedItems && selectedItems.length > 0) {
                            for (let item of selectedItems) {
                                draft.users.value.push(item as SelectableGraphMember);
                            }
                            draft.queryMembers[MembershipQuery.Direct] = draft.users;
                            draft.queryMembers[MembershipQuery.Expanded] = undefined;
                        }
                        break;
                    }
                }
                break;
            }

            case GroupMembersPivotActionTypes.DismissMessageCard: {
                draft.showMessageCard = false;
                break;
            }

            case GroupMembersPivotActionTypes.OpenAddGroupMemberPanel: {
                draft.showAddMemberPanel = true;
                break;
            }

            case GroupMembersPivotActionTypes.FetchOrgSettingsCompleted: {
                const settingsData = action.payload;
                let isAadOrg = false;
                if (settingsData.orgnizationTenantData.id) {
                    isAadOrg = settingsData.orgnizationTenantData.id != EmptyGuidString;
                }
                draft.isAadOrg = isAadOrg;
                draft.supportServicePrincipals = settingsData.supportServicePrincipals === true;
                break;
            }

            case GroupMembersPivotActionTypes.CloseAddGroupMemberPanel: {
                draft.showAddMemberPanel = false;
                draft.invalidMembers = [];
                draft.validMembers = [];
                break;
            }

            case GroupMembersPivotActionTypes.AddInvalidGroupMember: {
                const { member } = action.payload;
                if (draft.validMembers.findIndex(x => x.entityId === member.entityId) == -1) {
                    draft.validMembers.push(member);
                    draft.invalidMembers.push(member);
                }
                break;
            }

            case GroupMembersPivotActionTypes.AddValidGroupMember: {
                const { member } = action.payload;
                if (draft.validMembers.findIndex(x => x.entityId === member.entityId) == -1) {
                    draft.validMembers.push(member);
                }
                break;
            }

            case GroupMembersPivotActionTypes.RemoveGroupMembers: {
                draft.validMembers = draft.validMembers.filter(
                    entity => action.payload.members.filter(item => item.entityId === entity.entityId).length === 0
                );
                draft.invalidMembers = draft.invalidMembers.filter(
                    entity => action.payload.members.filter(item => item.entityId === entity.entityId).length === 0
                );
                break;
            }

            case GroupMembersPivotActionTypes.ChangeMembershipQuery: {
                draft.loading = true;
                break;
            }

            case GroupMembersPivotActionTypes.ChangeMembershipQueryCompleted: {
                const { members, query } = action.payload;
                draft.users = members;
                draft.queryMembers[query] = members;
                draft.loading = false;
            }
        }
    });
}

function updateSearchStatus(state: IGroupMembersPivotState, searchStatus: SearchStatus) {
    const { searchResult } = state;
    if (!searchResult) {
        return;
    }
    if (searchStatus === SearchStatus.None) {
        searchResult.resultItems = [];
        searchResult.keyword = "";
    }
    searchResult.status = searchStatus;
}

function updateSearchResult(state: IGroupMembersPivotState, keyword: string, results: any[], hasMore: boolean) {
    if (!keyword || !state.searchResult) {
        return;
    }
    state.searchResult = {
        keyword,
        resultItems: results,
        status: SearchStatus.Completed,
        hasMore
    };
}
